import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { registerViewType, type ViewProps } from "../../registry/viewTypes";
import type { FilterAst } from "../../core/filter";
import { dayKey } from "../../planner/ops";
import { Planner } from "../../planner/Planner";
import { DateNav } from "../../app/DateNav";
import { FILTER_SCHEMA } from "../shared";
import { SOURCE_SCHEMA, type Source } from "../elsewhere/source";

export interface CalendarWeekConfig {
  filter?: FilterAst;
  /** a week (default) or one day */
  span?: "week" | "day";
  showDue?: boolean;
  /** the read-only overlay of the ICS calendars in Settings → Calendars (M10) */
  showCalendars?: boolean;
  startHour?: number;
  endHour?: number;
  /** "Show from" (ADR 0024): other workspaces' planned and due tasks join the grid, named */
  source?: Source;
}

function mondayOf(d: Date): Date {
  const x = new Date(d);
  x.setHours(12, 0, 0, 0);
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  return x;
}

/**
 * The Week widget (spec §9.7 `calendar-week`): the planner (`planner/Planner.tsx`, the same component as the Planner
 * page) on a dashboard, a week or a day, with its own hours, filter, "Show from" and calendars. The trays sit beside
 * the grid when the widget is wide enough.
 */
export function CalendarWeekView({ config, ctx }: ViewProps<CalendarWeekConfig>) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const startHour = config.startHour ?? 7;
  const endHour = config.endHour ?? 21;
  const hours = Array.from({ length: Math.max(1, endHour - startHour) }, (_, i) => startHour + i);
  const span = config.span === "day" ? 1 : 7;
  const [offset, setOffset] = useState(0);
  const todayStr = new Date(ctx.now).toDateString(); // the shown period only changes at midnight
  const days = useMemo(() => {
    const start = span === 7 ? mondayOf(new Date(todayStr)) : new Date(todayStr);
    start.setDate(start.getDate() + offset * span);
    return Array.from({ length: span }, (_, i) => {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      return dayKey(d);
    });
  }, [offset, span, todayStr]);
  const todayKey = dayKey(new Date(ctx.now));
  const fmt = (d: string, o: Intl.DateTimeFormatOptions) =>
    new Date(d + "T12:00:00").toLocaleDateString(i18n.language, o);
  const label =
    span === 1
      ? fmt(days[0], { weekday: "long", day: "numeric", month: "long" })
      : `${fmt(days[0], { day: "numeric", month: "short" })} – ${fmt(days[6], { day: "numeric", month: "short" })}`;

  return (
    <div className="flex h-full min-h-0 flex-col gap-2" data-testid="calendar-week-view">
      <DateNav
        label={label}
        current={offset === 0}
        currentLabel={span === 1 ? t("planner.today") : t("planner.thisWeek")}
        prevLabel={span === 1 ? t("worklog.previousDay") : t("planner.previousWeek")}
        nextLabel={span === 1 ? t("worklog.nextDay") : t("planner.nextWeek")}
        onPrev={() => setOffset(offset - 1)}
        onNext={() => setOffset(offset + 1)}
        onCurrent={() => setOffset(0)}
      />
      <Planner
        handle={ctx.handle}
        workspaceId={ctx.workspaceId}
        readOnly={ctx.readOnly}
        days={days}
        hours={hours}
        layout={span === 1 ? "day" : "week"}
        todayKey={todayKey}
        now={ctx.now}
        filter={config.filter}
        source={config.source}
        showCalendars={config.showCalendars !== false}
        showDue={config.showDue !== false}
        narrowTrays="hidden"
        openTask={(id, ws) =>
          ws
            ? navigate(`/list?workspace=${encodeURIComponent(ws)}&task=${encodeURIComponent(id)}`)
            : ctx.openTask(id)
        }
        openDay={(d) => navigate(`/planner/${d}?view=day`)}
      />
    </div>
  );
}

registerViewType<CalendarWeekConfig>({
  id: "calendar-week",
  label: "views.calendar-week.label",
  icon: "calendar",
  level: "simple",
  defaultConfig: { span: "week", showDue: true, showCalendars: true, startHour: 7, endHour: 21 },
  configSchema: {
    type: "object",
    properties: {
      span: { type: "string", title: "config.span", enum: ["week", "day"] },
      showDue: { type: "boolean", title: "config.showDue" },
      showCalendars: { type: "boolean", title: "config.showCalendars" },
      startHour: { type: "number", title: "config.startHour", minimum: 0, maximum: 23 },
      endHour: { type: "number", title: "config.endHour", minimum: 1, maximum: 24 },
      source: SOURCE_SCHEMA,
      filter: FILTER_SCHEMA,
    },
  },
  Component: CalendarWeekView,
  minSize: { w: 6, h: 6 },
});
